import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, expand, map, reduce, retry, tap } from 'rxjs/operators';
import { StatementMessagingHttpClientService } from '../http-bridge/statement-messaging-http-client.service';
import { Fund, FundIdWithName, FundResponse } from '../../core/interfaces/fund.model';

@Injectable({
  providedIn: 'root',
})
export class FundDataService {
  private readonly httpClient = inject(StatementMessagingHttpClientService);

  // BehaviorSubject to hold fund IDs data
  private readonly fundIdsSubject = new BehaviorSubject<FundIdWithName[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);

  // Public observables
  public readonly fundIds$: Observable<FundIdWithName[]> = this.fundIdsSubject.asObservable();
  public readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();
  public readonly error$: Observable<string | null> = this.errorSubject.asObservable();

  // Constants
  private readonly FUND_ID_LIMIT = 500;
  private readonly MAX_RETRIES = 3;

  // Additional fund IDs to include (will be sorted along with API data)
  private readonly ADDITIONAL_FUND_IDS: FundIdWithName[] = [
    {
      fundPortId: '0000',
      fundFullName: '',
    },
    {
      fundPortId: 'VBS ',
      fundFullName: 'Vanguard Brokerage Wealth First Accounts',
    }
  ];

  /**
   * Load all fund IDs - call this at application boot
   */
  loadFundIds(): Observable<FundIdWithName[]> {
    // If already loaded, return cached data
    if (this.fundIdsSubject.value.length > 0) {
      return of(this.fundIdsSubject.value);
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return this.fetchAllFundIds().pipe(
      tap((fundIds) => {
        // Add additional fund IDs and sort all together
        const allFundIds = this.addAdditionalFundIds(fundIds);
        const sortedFundIds = this.sortFundIds(allFundIds);
        this.fundIdsSubject.next(sortedFundIds);
        this.loadingSubject.next(false);
      }),
      catchError((error: Error) => {
        this.errorSubject.next(error.message);
        this.loadingSubject.next(false);
        // On error, return only additional fund IDs sorted
        const sortedAdditional = this.sortFundIds([...this.ADDITIONAL_FUND_IDS]);
        this.fundIdsSubject.next(sortedAdditional);
        return of(sortedAdditional);
      }),
    );
  }

  /**
   * Add additional fund IDs to the list (removing duplicates)
   */
  private addAdditionalFundIds(fundIds: FundIdWithName[]): FundIdWithName[] {
    // Filter out any duplicates from API data
    const filteredFundIds = fundIds.filter(
      (fund) => !this.ADDITIONAL_FUND_IDS.some((additional) => additional.fundPortId === fund.fundPortId)
    );

    // Combine additional fund IDs with API fund IDs
    return [...this.ADDITIONAL_FUND_IDS, ...filteredFundIds];
  }

  /**
   * Sort fund IDs alphabetically by fundPortId
   * Empty string will naturally sort first, VBS will sort in its alphabetical position
   */
  private sortFundIds(fundIds: FundIdWithName[]): FundIdWithName[] {
    return fundIds.sort((a, b) => {
      const idA = a.fundPortId.toUpperCase();
      const idB = b.fundPortId.toUpperCase();

      if (idA < idB) return -1;
      if (idA > idB) return 1;
      return 0;
    });
  }

  /**
   * Fetch all fund IDs with pagination using expand operator
   */
  private fetchAllFundIds(): Observable<FundIdWithName[]> {
    return this.fetchFundIdsBatch(0, this.FUND_ID_LIMIT).pipe(
      expand((response) => {
        const metadata = response.data.findByDistributionGroups.metadata;
        const currentFundsCount = response.data.findByDistributionGroups.funds.length;
        const hasMore = metadata.nextIndex !== null && metadata.nextIndex > metadata.offset;

        if (hasMore) {
          const totalSoFar = metadata.offset + currentFundsCount;
          console.log(
            `→ Fetching next batch: offset=${metadata.nextIndex}, total fetched=${totalSoFar}`,
          );
          return this.fetchFundIdsBatch(metadata.nextIndex, this.FUND_ID_LIMIT);
        }
        return of(); // Complete the expand
      }),
      map((response) => this.extractFundIds(response.data.findByDistributionGroups.funds)),
      reduce((acc: FundIdWithName[], curr: FundIdWithName[]) => [...acc, ...curr], []),
      retry({ count: this.MAX_RETRIES, delay: 1000, resetOnSuccess: true }),
    );
  }

  /**
   * Alternative implementation using async/await for better readability
   */
  // eslint-disable-next-line complexity,max-statements
  private async fetchAllFundIdsAsync(): Promise<FundIdWithName[]> {
    const allFundIds: FundIdWithName[] = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
      const response = await this.httpClient
        .getFundIdData(offset, this.FUND_ID_LIMIT)
        .toPromise();

      if (!response) {
        break;
      }

      const funds = response.data.findByDistributionGroups.funds;
      const metadata = response.data.findByDistributionGroups.metadata;

      const fundIds = this.extractFundIds(funds);
      allFundIds.push(...fundIds);

      console.log(
        `Fetched ${fundIds.length} fund IDs (offset: ${offset}, total: ${allFundIds.length})`,
      );

      // Check if there are more results
      hasMore = metadata.nextIndex !== null && metadata.nextIndex > offset;
      offset = metadata.nextIndex || offset;

      // Safety check to prevent infinite loops
      if (offset === 0 || !hasMore) {
        break;
      }
    }

    return allFundIds;
  }

  /**
   * Load fund IDs using async/await approach (alternative)
   */
  loadFundIdsAsync(): Observable<FundIdWithName[]> {
    // If already loaded, return cached data
    if (this.fundIdsSubject.value.length > 0) {
      return of(this.fundIdsSubject.value);
    }

    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    return new Observable<FundIdWithName[]>((subscriber) => {
      this.fetchAllFundIdsAsync()
        .then((fundIds) => {
          // Add additional fund IDs and sort all together
          const allFundIds = this.addAdditionalFundIds(fundIds);
          const sortedFundIds = this.sortFundIds(allFundIds);
          this.fundIdsSubject.next(sortedFundIds);
          this.loadingSubject.next(false);
          subscriber.next(sortedFundIds);
          subscriber.complete();
        })
        .catch((error: Error) => {
          this.errorSubject.next(error.message);
          this.loadingSubject.next(false);
          // On error, return only additional fund IDs sorted
          const sortedAdditional = this.sortFundIds([...this.ADDITIONAL_FUND_IDS]);
          this.fundIdsSubject.next(sortedAdditional);
          subscriber.next(sortedAdditional);
          subscriber.complete();
        });
    });
  }

  /**
   * Fetch a batch of fund IDs
   */
  private fetchFundIdsBatch(offset: number, limit: number): Observable<FundResponse> {
    return this.httpClient.getFundIdData(offset, limit);
  }

  /**
   * Extract fund IDs from fund list
   */
  private extractFundIds(funds: Fund[]): FundIdWithName[] {
    if (!funds || funds.length === 0) {
      return [];
    }

    return funds.map((fund) => ({
      fundPortId: fund.fundPortId,
      fundFullName: fund.profile?.fundFullName || fund.fundPortId,
    }));
  }

  /**
   * Get current fund IDs value (synchronous)
   */
  getFundIds(): FundIdWithName[] {
    return this.fundIdsSubject.value;
  }

  /**
   * Check if fund IDs are loaded
   */
  isFundIdsLoaded(): boolean {
    return this.fundIdsSubject.value.length > 0;
  }

  /**
   * Get current loading state (synchronous)
   */
  isLoading(): boolean {
    return this.loadingSubject.value;
  }

  /**
   * Get current error (synchronous)
   */
  getError(): string | null {
    return this.errorSubject.value;
  }

  /**
   * Clear fund IDs cache
   */
  clearCache(): void {
    this.fundIdsSubject.next([]);
    this.errorSubject.next(null);
  }

  /**
   * Reload fund IDs (clears cache and fetches again)
   */
  reload(): Observable<FundIdWithName[]> {
    this.clearCache();
    return this.loadFundIds();
  }
}
